import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowLeft, CalendarDays, CreditCard, Edit3, FileText, Plus, Save, Trash2, ClipboardList,
  CheckCircle, XCircle, Clock, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectValue, SelectTrigger, SelectContent, SelectItem } from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { DatePicker } from '../../components/ui/date-picker';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import ModuleAttendanceCalendar from '../../components/ModuleAttendanceCalendar';
import SharedFeesTab from '../../components/SharedFeesTab';
import { cn } from '../../lib/utils';
import { formatCurrency, formatDate } from '../../lib/utils';
import { getModuleConfig, type ModuleKey } from '../../lib/moduleConfig';

const CLASS_NAMES: Record<string, string> = {
  '1': 'Class 6', '2': 'Class 7', '3': 'Class 8',
  '4': 'Class 9', '5': 'Class 10', '6': 'Class 11', '7': 'Class 12',
};

function StudentHeader({ student, moduleKey, classId, batchId }: any) {
  const config = getModuleConfig(moduleKey);
  const navigate = useNavigate();
  const displayClass = CLASS_NAMES[classId] || `Class ${classId}`;

  return (
    <div className={`rounded-xl bg-gradient-to-br ${config.gradient} p-4 text-white shadow-lg`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-white/70">{config.label} / {displayClass}</p>
          <h1 className="text-xl font-black truncate">{student?.student_name || 'Student'}</h1>
          <p className="text-xs text-white/80 mt-1">{student?.batch_name || student?.section_name || ''}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="secondary" className="bg-white/20 text-white hover:bg-white/30 border-white/20" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Button asChild size="sm" variant="secondary" className="bg-white/20 text-white hover:bg-white/30 border-white/20">
            <Link to={`/modules/${moduleKey}/classes/${classId}/batches/${batchId}/students/${student?.id}/edit`}><Edit3 className="h-4 w-4" /></Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

// =================== EXAMS MODULE ===================
function ExamsPanel({ studentId, classId, batchId }: { studentId: string; classId?: string | null; batchId?: string | null }) {
  const queryClient = useQueryClient();
  const [showExamForm, setShowExamForm] = useState(false);
  const [examForm, setExamForm] = useState({ name: '', examDate: '', totalMarks: '' });
  const [marks, setMarks] = useState<Record<number, string>>({});
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'exam' | 'result'; id: number; name?: string } | null>(null);

  const queryKey = ['student-exams-module', studentId, classId, batchId];
  const { data: exams, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (classId) params.set('classId', classId);
      if (batchId) params.set('batchId', batchId);
      const res = await api.get<any>(`/api/exams/student/${studentId}?${params.toString()}`);
      return res.data || [];
    },
  });

  const addExam = useMutation({
    mutationFn: async () => api.post('/api/exams', {
      name: examForm.name,
      classId: Number(classId),
      batchId: batchId ? Number(batchId) : undefined,
      examDate: examForm.examDate || undefined,
      totalMarks: Number(examForm.totalMarks),
    }),
    onSuccess: () => {
      toast.success('Exam added');
      setExamForm({ name: '', examDate: '', totalMarks: '' });
      setShowExamForm(false);
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const saveResult = useMutation({
    mutationFn: async (exam: any) => api.post('/api/exams/results', {
      examId: exam.id,
      studentId: Number(studentId),
      obtainedMarks: Number(marks[exam.id] ?? exam.obtained_marks ?? 0),
    }),
    onSuccess: () => { toast.success('Marks saved'); queryClient.invalidateQueries({ queryKey }); },
  });

  const deleteExam = useMutation({
    mutationFn: async (id: number) => api.delete(`/api/exams/${id}`),
    onSuccess: () => { toast.success('Exam deleted'); setDeleteTarget(null); queryClient.invalidateQueries({ queryKey }); },
  });

  const deleteResult = useMutation({
    mutationFn: async (id: number) => api.delete(`/api/exams/results/${id}`),
    onSuccess: () => { toast.success('Result cleared'); setDeleteTarget(null); queryClient.invalidateQueries({ queryKey }); },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-black text-light-text dark:text-dark-text flex items-center gap-2 text-lg">
          <ClipboardList className="h-5 w-5 text-rose-500" /> Exam Records
        </h2>
        <Button size="sm" onClick={() => setShowExamForm(true)}><Plus className="h-4 w-4" /> Add Exam</Button>
      </div>

      {showExamForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">New Exam</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input placeholder="Exam name" value={examForm.name} onChange={(e) => setExamForm({ ...examForm, name: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <DatePicker value={examForm.examDate} onChange={(v) => setExamForm({ ...examForm, examDate: v })} placeholder="Exam date" />
              <Input type="number" placeholder="Total marks" value={examForm.totalMarks} onChange={(e) => setExamForm({ ...examForm, totalMarks: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => addExam.mutate()} disabled={!examForm.name || !examForm.totalMarks}>Save Exam</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowExamForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 rounded-xl bg-light-elevated dark:bg-dark-elevated animate-pulse" />
          ))}
        </div>
      ) : (exams || []).length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center text-light-text-muted">
            No exams yet. Add the first exam for this class/batch.
          </CardContent>
        </Card>
      ) : (
        (exams || []).map((exam: any) => (
          <motion.div
            key={exam.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="text-base truncate">{exam.name}</CardTitle>
                    <p className="text-xs text-light-text-muted mt-0.5">
                      Total {exam.total_marks} marks{exam.exam_date ? ` • ${formatDate(exam.exam_date)}` : ''}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-rose-600 h-8 w-8 p-0 shrink-0"
                    onClick={() => setDeleteTarget({ type: 'exam', id: exam.id, name: exam.name })}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-xl bg-lime-50 dark:bg-lime-500/10 p-2.5 text-center">
                    <p className="text-[10px] text-lime-700 dark:text-lime-300">Obtained</p>
                    <p className="font-black text-sm">{exam.obtained_marks ?? '-'}</p>
                  </div>
                  <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10 p-2.5 text-center">
                    <p className="text-[10px] text-amber-700 dark:text-amber-300">Rank</p>
                    <p className="font-black text-sm">{exam.obtained_marks == null ? '-' : exam.rank}</p>
                  </div>
                  <div className="rounded-xl bg-purple-50 dark:bg-purple-500/10 p-2.5 text-center">
                    <p className="text-[10px] text-purple-700 dark:text-purple-300">Percent</p>
                    <p className="font-black text-sm">
                      {exam.obtained_marks == null ? '-' : `${Math.round((exam.obtained_marks / exam.total_marks) * 100)}%`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    className="min-w-0 flex-1 h-10"
                    placeholder="Obtained marks"
                    value={marks[exam.id] ?? exam.obtained_marks ?? ''}
                    onChange={(e) => setMarks({ ...marks, [exam.id]: e.target.value })}
                  />
                  <Button size="sm" onClick={() => saveResult.mutate(exam)}><Save className="h-3.5 w-3.5" /> Save</Button>
                  {exam.result_id && (
                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setDeleteTarget({ type: 'result', id: exam.result_id, name: `result for ${exam.name}` })}>
                      <XCircle className="h-3.5 w-3.5" /> Clear
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === 'exam'
                ? `Delete exam "${deleteTarget.name}"? All results will be lost. This cannot be undone.`
                : deleteTarget?.type === 'result'
                ? `Clear ${deleteTarget.name}? This cannot be undone.`
                : 'This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-500 hover:bg-rose-600 text-white"
              onClick={() => {
                if (deleteTarget?.type === 'exam') deleteExam.mutate(deleteTarget.id);
                else if (deleteTarget?.type === 'result') deleteResult.mutate(deleteTarget.id);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// =================== PAYMENTS MODULE ===================
function PaymentsPanel({ studentId, classId, batchId }: { studentId: string; classId?: string | null; batchId?: string | null }) {
  const queryClient = useQueryClient();
  const queryKey = ['module-payments', studentId];
  const { data: invoices, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await api.get<any>(`/api/fees/invoices?studentId=${studentId}`)).data || [],
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          {[1, 2, 3].map((i) => (<div key={i} className="h-14 rounded-xl bg-light-elevated dark:bg-dark-elevated animate-pulse" />))}
        </div>
        {[1, 2].map((i) => (<div key={i} className="h-16 rounded-xl bg-light-elevated dark:bg-dark-elevated animate-pulse" />))}
      </div>
    );
  }

  return <SharedFeesTab studentId={studentId} invoices={invoices || []} queryKey={queryKey} allowCreateInvoice />;
}

// =================== ATTENDANCE MODULE ===================
function AttendancePanel({ studentId, classId, batchId }: { studentId: string; classId?: string | null; batchId?: string | null }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], status: 'present', notes: '' });
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

  const queryKey = ['module-attendance-student', studentId];
  const { data: records, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await api.get<any>(`/api/attendance/student/${studentId}`)).data || [],
  });

  const save = useMutation({
    mutationFn: async () => api.post('/api/attendance/mark', {
      date: form.date,
      classId: Number(classId),
      batchId: batchId ? Number(batchId) : undefined,
      records: [{ studentId: Number(studentId), status: form.status, notes: form.notes }],
    }),
    onSuccess: () => {
      toast.success('Attendance saved');
      queryClient.invalidateQueries({ queryKey });
      queryClient.refetchQueries({ queryKey });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: number) => api.delete(`/api/attendance/${id}`),
    onSuccess: () => {
      toast.success('Attendance deleted');
      setDeleteTargetId(null);
      queryClient.invalidateQueries({ queryKey });
      queryClient.refetchQueries({ queryKey });
    },
  });

  const stats = useMemo(() => {
    const counts = { present: 0, absent: 0, late: 0, leave: 0, total: 0 };
    (records || []).forEach((r: any) => {
      counts.total++;
      if (counts[r.status as keyof typeof counts] !== undefined) {
        counts[r.status as keyof typeof counts]++;
      }
    });
    return counts;
  }, [records]);

  const statCards = [
    { key: 'present', color: 'bg-lime-50 dark:bg-lime-500/10', text: 'text-lime-700 dark:text-lime-300', label: 'Present', icon: CheckCircle },
    { key: 'absent', color: 'bg-rose-50 dark:bg-rose-500/10', text: 'text-rose-700 dark:text-rose-300', label: 'Absent', icon: XCircle },
    { key: 'late', color: 'bg-amber-50 dark:bg-amber-500/10', text: 'text-amber-700 dark:text-amber-300', label: 'Late', icon: Clock },
    { key: 'leave', color: 'bg-sky-50 dark:bg-sky-500/10', text: 'text-sky-700 dark:text-sky-300', label: 'Leave', icon: AlertTriangle },
  ];

  return (
    <div className="space-y-3">
      <h2 className="font-black text-light-text dark:text-dark-text flex items-center gap-2 text-lg">
        <CalendarDays className="h-5 w-5 text-cyan-500" /> Attendance
      </h2>

      <div className="grid grid-cols-4 gap-2">
        {statCards.map((s) => (
          <motion.div
            key={s.key}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className={`rounded-xl ${s.color} p-2.5 text-center`}
          >
            <p className={`text-[10px] font-medium ${s.text}`}>{s.label}</p>
            <p className="font-black text-sm">{stats[s.key as keyof typeof stats]}</p>
          </motion.div>
        ))}
      </div>

      {isLoading ? (
        <div className="h-64 rounded-xl bg-light-elevated dark:bg-dark-elevated animate-pulse" />
      ) : (
        <ModuleAttendanceCalendar records={(records || []).map((r: any) => ({ attendance_date: r.attendance_date, status: r.status, notes: r.notes }))} />
      )}

      {/* Add attendance form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Mark Attendance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })} placeholder="Select date" />
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="h-11 min-h-[44px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="present">Present</SelectItem>
                <SelectItem value="absent">Absent</SelectItem>
                <SelectItem value="late">Late</SelectItem>
                <SelectItem value="leave">Leave</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Input placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <Button onClick={() => save.mutate()} disabled={!classId || save.isPending} className="w-full">
            {save.isPending ? 'Saving...' : 'Mark Attendance'}
          </Button>
        </CardContent>
      </Card>

      {/* Recent records */}
      <div className="space-y-2">
        <h3 className="font-semibold text-sm text-light-text dark:text-dark-text">Recent records</h3>
        {(records || []).slice(0, 15).map((record: any) => (
          <Card key={record.id} className="overflow-hidden">
            <CardContent className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30, delay: 0.05 }}
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center shrink-0',
                    record.status === 'present' && 'bg-lime-100 dark:bg-lime-500/20 text-lime-600',
                    record.status === 'absent' && 'bg-rose-100 dark:bg-rose-500/20 text-rose-600',
                    record.status === 'late' && 'bg-amber-100 dark:bg-amber-500/20 text-amber-600',
                    record.status === 'leave' && 'bg-sky-100 dark:bg-sky-500/20 text-sky-600'
                  )}
                >
                  {record.status === 'present' ? <CheckCircle className="w-4 h-4" /> :
                   record.status === 'absent' ? <XCircle className="w-4 h-4" /> :
                   record.status === 'late' ? <Clock className="w-4 h-4" /> :
                   <AlertTriangle className="w-4 h-4" />}
                </motion.div>
                <div>
                  <p className="font-semibold text-sm">{formatDate(record.attendance_date)}</p>
                  <p className={cn(
                    'text-xs capitalize',
                    record.status === 'present' ? 'text-lime-600' :
                    record.status === 'absent' ? 'text-rose-600' :
                    record.status === 'late' ? 'text-amber-600' : 'text-sky-600'
                  )}>
                    {record.status}
                    {record.notes && <span className="text-light-text-muted"> — {record.notes}</span>}
                  </p>
                </div>
              </div>
              <Button size="sm" variant="ghost" className="text-rose-600 h-8 w-8 p-0" onClick={() => setDeleteTargetId(record.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={!!deleteTargetId} onOpenChange={() => setDeleteTargetId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Attendance Record</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this attendance record? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-500 hover:bg-rose-600 text-white"
              onClick={() => deleteTargetId && remove.mutate(deleteTargetId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// =================== STUDENTS MODULE ===================
// Students module redirects to full StudentProfile page (/students/:id)
// This placeholder is for edge cases only
function StudentsPanel({ studentId }: { studentId: string }) {
  const navigate = useNavigate();
  useEffect(() => { navigate(`/students/${studentId}`, { replace: true }); }, [studentId, navigate]);
  return <div className="text-center py-8 text-light-text-muted">Opening full profile...</div>;
}

// =================== MAIN PAGE ===================
export default function ModuleStudentDetailPage() {
  const { module, studentId, classId: pathClassId, batchId: pathBatchId } = useParams();
  const [searchParams] = useSearchParams();
  const moduleKey = (module || 'students') as ModuleKey;
  const classId = pathClassId || searchParams.get('classId');
  const batchId = pathBatchId || searchParams.get('batchId');

  const { data: student } = useQuery({
    queryKey: ['module-student-detail', studentId],
    queryFn: async () => (await api.get<any>(`/api/students/${studentId}`)).data,
    enabled: !!studentId,
    staleTime: 0,
  });

  return (
    <div className="space-y-4 pb-4">
      <StudentHeader student={student} moduleKey={moduleKey} classId={classId} batchId={batchId} />
      {moduleKey === 'students' && <StudentsPanel studentId={studentId!} />}
      {moduleKey === 'exams' && <ExamsPanel studentId={studentId!} classId={classId} batchId={batchId} />}
      {moduleKey === 'payments' && <PaymentsPanel studentId={studentId!} classId={classId} batchId={batchId} />}
      {moduleKey === 'attendance' && <AttendancePanel studentId={studentId!} classId={classId || String(student?.class_id)} batchId={batchId || String(student?.batch_id)} />}
    </div>
  );
}
